// Subdomain labels a club site can never claim (clubname.blindvalet.site).
// KEEP IN SYNC: blindvalet-ng/src/app/website/reserved-slugs.ts and
// blindvalet-sites/src/lib/reserved-slugs.ts are identical files (the
// renderer's Playwright suite asserts it). Existing sites keep their slug
// even if a later list would block it; the checks run on claim only.

// Exact labels, grouped by why they are reserved.
export const RESERVED_SLUG_WORDS: readonly string[] = [
  // Infrastructure, mail, environments
  "www", "app", "admin", "api", "sites", "site", "mail", "email", "smtp", "imap", "pop", "pop3",
  "ftp", "sftp", "mx", "ns", "ns1", "ns2", "dns", "cdn", "static", "assets", "media", "img",
  "images", "files", "cache", "status", "health", "ping", "test", "tests", "testing", "staging",
  "stage", "dev", "develop", "development", "preview", "beta", "alpha", "sandbox", "demo",
  "localhost", "internal", "intranet", "vpn", "proxy", "gateway", "edge", "origin", "backup",
  // Product surfaces we use or may use
  "clock", "live", "tv", "display", "screen", "timer", "blinds", "tournament", "tournaments",
  "league", "leagues", "club", "clubs", "player", "players", "member", "members", "results",
  "standings", "rankings", "ranking", "leaderboard", "embed", "embeds", "widget", "widgets",
  "share", "join", "invite", "invites", "register", "signup", "sign-up", "signin", "sign-in",
  "login", "logout", "auth", "oauth", "sso", "account", "accounts", "profile", "profiles", "me",
  "my", "dashboard", "console", "backoffice", "manage", "manager", "store", "shop", "checkout",
  "cart", "pay", "payment", "payments", "billing", "invoice", "invoices", "subscribe",
  "subscription", "subscriptions", "pricing", "price", "prices", "plans", "plan", "free", "pro",
  "premium", "business", "enterprise", "partner", "partners", "affiliate", "affiliates",
  "developer", "developers", "docs", "doc", "documentation", "help", "helpcenter", "help-center",
  "support", "faq", "faqs", "contact", "feedback", "forum", "forums", "community", "blog", "news",
  "press", "about", "legal", "terms", "privacy", "security", "abuse", "dmca", "careers", "jobs",
  "download", "downloads", "update", "updates", "mobile", "ios", "android", "desktop", "print",
  "printer", "kaio", "sealegs", "sea-legs", "homepokertournament",
  // Brand and trust
  "blindvalet", "blind-valet", "brickriverlab", "brick-river-lab", "official", "staff",
  "moderator", "moderators", "mod", "mods", "root", "sysadmin", "system", "webmaster",
  "postmaster", "hostmaster", "noreply", "no-reply", "info", "sales", "marketing", "hello",
  "team", "founder", "ceo", "owner", "verified", "trust", "safety",
  // Country and marketing labels
  "us", "uk", "eu", "ca", "au", "usa", "france", "europe", "world", "global", "international",
  // Abuse magnets
  "casino", "casinos", "bet", "bets", "betting", "gamble", "gambling", "crypto", "bitcoin",
  "win", "winner", "winners", "jackpot", "bonus", "promo", "vip", "adult", "xxx", "sex", "porn",
  "nude", "escort",
];

// Every language the app ships in (plus base codes): kept for localized
// marketing pages.
export const RESERVED_LANGUAGE_CODES: readonly string[] = [
  "ar", "bg", "bn", "ca", "cs", "da", "de", "el", "en", "es", "et", "fi", "fr", "gu", "he",
  "hi", "hr", "hu", "id", "it", "ja", "kn", "ko", "lt", "lv", "ml", "mr", "ms", "nl", "no",
  "pa", "pl", "pt", "pt-br", "pt-pt", "ro", "ru", "sk", "sl", "sr", "sv", "ta", "te", "th",
  "tr", "uk", "vi", "zh", "zh-cn", "zh-tw",
];

// Labels containing these are refused outright (brand impersonation).
export const RESERVED_SLUG_SUBSTRINGS: readonly string[] = ["blindvalet", "blind-valet"];
// Labels starting with these are refused (our own prefixes).
export const RESERVED_SLUG_PREFIXES: readonly string[] = ["bv-"];

const RESERVED = new Set<string>([...RESERVED_SLUG_WORDS, ...RESERVED_LANGUAGE_CODES]);

// Shape rule: 3-63 chars, lowercase letters/digits/hyphens, no leading or
// trailing hyphen (valid DNS label, no confusing punctuation).
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/;

export function isValidSlugShape(slug: string): boolean {
  return SLUG_PATTERN.test(slug);
}

export function isReservedSlug(slug: string): boolean {
  const s = String(slug || "").toLowerCase();
  if (RESERVED.has(s) || s === "bv") return true;
  if (RESERVED_SLUG_SUBSTRINGS.some((x) => s.includes(x))) return true;
  if (RESERVED_SLUG_PREFIXES.some((x) => s.startsWith(x))) return true;
  return false;
}

// Claimable = well-formed and not reserved (availability is checked separately).
export function isClaimableSlug(slug: string): boolean {
  return isValidSlugShape(slug) && !isReservedSlug(slug);
}
